import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from '@/components/charts/recharts';
import { Home, Car, GraduationCap, CreditCard, ArrowRight, AlertCircle, Calculator } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function EMICalculator() {
  const [loanAmount, setLoanAmount] = useState(5000000);
  const [interestRate, setInterestRate] = useState(8.5);
  const [tenure, setTenure] = useState(20);
  const [tenureType, setTenureType] = useState('years');
  
  // Input display states
  const [amountInput, setAmountInput] = useState('50,00,000');
  const [rateInput, setRateInput] = useState('8.5');
  const [tenureInput, setTenureInput] = useState('20');
  
  // Error states
  const [errors, setErrors] = useState({});

  // Indian number formatting
  const formatIndianNumber = (num) => {
    if (num === null || num === undefined || isNaN(num)) return '';
    const numStr = Math.round(num).toString();
    let lastThree = numStr.substring(numStr.length - 3);
    let otherNumbers = numStr.substring(0, numStr.length - 3);
    if (otherNumbers !== '') lastThree = ',' + lastThree;
    return otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + lastThree;
  };

  const formatCurrency = (value) => {
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(2)} Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(2)} L`;
    return `₹${formatIndianNumber(value)}`;
  };

  // Input handlers
  const handleAmountChange = (e) => {
    const raw = e.target.value.replace(/,/g, '');
    setAmountInput(e.target.value);
    const num = parseInt(raw, 10);
    if (!isNaN(num) && num >= 10000 && num <= 500000000) {
      setLoanAmount(num);
      setErrors(prev => ({ ...prev, amount: '' }));
    } else if (raw === '') {
      setErrors(prev => ({ ...prev, amount: '' }));
    } else {
      setErrors(prev => ({ ...prev, amount: 'Enter amount between ₹10,000 and ₹50 Cr' }));
    }
  };

  const handleAmountBlur = () => {
    const num = parseInt(amountInput.replace(/,/g, ''), 10);
    const clamped = Math.max(10000, Math.min(500000000, num || 10000));
    setLoanAmount(clamped);
    setAmountInput(formatIndianNumber(clamped));
    setErrors(prev => ({ ...prev, amount: '' }));
  };

  const handleRateChange = (e) => {
    setRateInput(e.target.value);
    const num = parseFloat(e.target.value);
    if (!isNaN(num) && num >= 1 && num <= 30) {
      setInterestRate(num);
      setErrors(prev => ({ ...prev, rate: '' }));
    } else if (e.target.value === '') {
      setErrors(prev => ({ ...prev, rate: '' }));
    } else {
      setErrors(prev => ({ ...prev, rate: 'Interest rate must be between 1% and 30%' }));
    }
  };

  const handleRateBlur = () => {
    const num = parseFloat(rateInput) || 1;
    const clamped = Math.max(1, Math.min(30, num));
    setInterestRate(clamped);
    setRateInput(clamped.toString());
    setErrors(prev => ({ ...prev, rate: '' }));
  };

  const handleTenureChange = (e) => {
    setTenureInput(e.target.value);
    const num = parseInt(e.target.value, 10);
    const maxTenure = tenureType === 'years' ? 30 : 360;
    if (!isNaN(num) && num >= 1 && num <= maxTenure) {
      setTenure(num);
      setErrors(prev => ({ ...prev, tenure: '' }));
    } else if (e.target.value === '') {
      setErrors(prev => ({ ...prev, tenure: '' }));
    } else {
      setErrors(prev => ({ ...prev, tenure: `Tenure must be between 1 and ${maxTenure} ${tenureType}` }));
    }
  };

  const handleTenureBlur = () => {
    const num = parseInt(tenureInput, 10) || 1;
    const maxTenure = tenureType === 'years' ? 30 : 360;
    const clamped = Math.max(1, Math.min(maxTenure, num));
    setTenure(clamped);
    setTenureInput(clamped.toString());
    setErrors(prev => ({ ...prev, tenure: '' }));
  };

  const results = useMemo(() => {
    const months = tenureType === 'years' ? tenure * 12 : tenure;
    const monthlyRate = interestRate / 100 / 12;
    
    // EMI Formula: P * r * (1 + r)^n / ((1 + r)^n - 1)
    const emi = loanAmount * monthlyRate * Math.pow(1 + monthlyRate, months) / (Math.pow(1 + monthlyRate, months) - 1);
    
    const totalPayment = emi * months;
    const totalInterest = totalPayment - loanAmount;
    
    // Generate yearly breakdown
    const yearlyData = [];
    let remainingPrincipal = loanAmount;
    let totalPrincipalPaid = 0;
    let totalInterestPaid = 0;
    
    const yearsCount = tenureType === 'years' ? tenure : Math.ceil(tenure / 12);
    
    for (let year = 1; year <= yearsCount; year++) {
      const monthsInYear = tenureType === 'years' ? 12 : Math.min(12, tenure - (year - 1) * 12);
      let yearPrincipal = 0;
      let yearInterest = 0;
      
      for (let m = 0; m < monthsInYear; m++) {
        const interestPayment = remainingPrincipal * monthlyRate;
        const principalPayment = emi - interestPayment;
        
        yearInterest += interestPayment;
        yearPrincipal += principalPayment;
        remainingPrincipal -= principalPayment;
      }
      
      totalPrincipalPaid += yearPrincipal;
      totalInterestPaid += yearInterest;
      
      yearlyData.push({
        year: `Year ${year}`,
        yearNum: year,
        principal: Math.round(yearPrincipal),
        interest: Math.round(yearInterest),
        totalPaid: Math.round(totalPrincipalPaid + totalInterestPaid),
        balance: Math.max(0, Math.round(remainingPrincipal)),
      });
    }

    return {
      emi: Math.round(emi),
      totalPayment: Math.round(totalPayment),
      totalInterest: Math.round(totalInterest),
      principalPercent: ((loanAmount / totalPayment) * 100).toFixed(1),
      interestPercent: ((totalInterest / totalPayment) * 100).toFixed(1),
      yearlyData,
    };
  }, [loanAmount, interestRate, tenure, tenureType]);

  const pieData = [
    { name: 'Principal', value: loanAmount, color: '#F5A623' },
    { name: 'Interest', value: results.totalInterest, color: '#ef4444' },
  ];

  const loanTypes = [
    { icon: Home, label: 'Home Loan', rate: '8.5%' },
    { icon: Car, label: 'Car Loan', rate: '9.5%' },
    { icon: GraduationCap, label: 'Education Loan', rate: '10.5%' },
    { icon: CreditCard, label: 'Personal Loan', rate: '12%' },
  ];

  return (
    <div className="min-h-screen bg-background pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-heading text-5xl md:text-6xl font-bold text-text-primary mb-4">
            <Calculator className="inline-block h-12 w-12 text-primary mr-3" />
            EMI Calculator
          </h1>
          <p className="text-text-secondary text-lg max-w-2xl mx-auto">
            Calculate your Equated Monthly Installment for home loans, car loans, personal loans, and more
          </p>
        </div>

        {/* Quick Loan Types */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {loanTypes.map((loan, index) => (
            <button
              key={index}
              onClick={() => {
                const rate = parseFloat(loan.rate);
                setInterestRate(rate);
                setRateInput(rate.toString());
              }}
              className="glass-effect rounded-xl p-4 text-center hover:border-primary/50 transition-all"
            >
              <loan.icon className="h-8 w-8 text-primary mx-auto mb-2" />
              <p className="text-text-primary font-medium text-sm">{loan.label}</p>
              <p className="text-text-muted text-xs">{loan.rate} p.a.</p>
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Input Section */}
          <div className="glass-effect rounded-2xl p-8">
            <h2 className="font-heading text-2xl font-bold text-text-primary mb-6">Loan Details</h2>
            
            <div className="space-y-6">
              {/* Loan Amount */}
              <div>
                <label className="block text-text-secondary text-sm mb-2">Loan Amount (₹)</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={amountInput}
                  onChange={handleAmountChange}
                  onBlur={handleAmountBlur}
                  onFocus={() => setAmountInput(loanAmount.toString())}
                  className={`w-full bg-white/5 border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary ${errors.amount ? 'border-red-500' : 'border-white/10'}`}
                  data-testid="emi-loan-amount"
                />
                {errors.amount && (
                  <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" /> {errors.amount}
                  </p>
                )}
                <input
                  type="range"
                  min="100000"
                  max="50000000"
                  step="100000"
                  value={loanAmount}
                  onChange={(e) => {
                    setLoanAmount(parseInt(e.target.value));
                    setAmountInput(formatIndianNumber(parseInt(e.target.value)));
                  }}
                  className="w-full mt-2 accent-primary"
                />
              </div>

              {/* Interest Rate */}
              <div>
                <label className="block text-text-secondary text-sm mb-2">Interest Rate (% p.a.)</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={rateInput}
                  onChange={handleRateChange}
                  onBlur={handleRateBlur}
                  onFocus={() => setRateInput(interestRate.toString())}
                  className={`w-full bg-white/5 border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary ${errors.rate ? 'border-red-500' : 'border-white/10'}`}
                  data-testid="emi-interest-rate"
                />
                {errors.rate && (
                  <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" /> {errors.rate}
                  </p>
                )}
                <input
                  type="range"
                  min="1"
                  max="30"
                  step="0.1"
                  value={interestRate}
                  onChange={(e) => {
                    setInterestRate(parseFloat(e.target.value));
                    setRateInput(e.target.value);
                  }}
                  className="w-full mt-2 accent-primary"
                />
              </div>

              {/* Tenure */}
              <div>
                <label className="block text-text-secondary text-sm mb-2">Loan Tenure</label>
                <div className="flex gap-4">
                  <div className="flex-1">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={tenureInput}
                      onChange={handleTenureChange}
                      onBlur={handleTenureBlur}
                      onFocus={() => setTenureInput(tenure.toString())}
                      className={`w-full bg-white/5 border rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary ${errors.tenure ? 'border-red-500' : 'border-white/10'}`}
                      data-testid="emi-tenure"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setTenureType('years');
                        if (tenure > 30) {
                          setTenure(30);
                          setTenureInput('30');
                        }
                      }}
                      className={`px-4 py-2 rounded-lg transition-all ${tenureType === 'years' ? 'bg-primary text-white' : 'bg-white/5 text-text-secondary'}`}
                    >
                      Years
                    </button>
                    <button
                      onClick={() => {
                        setTenureType('months');
                        if (tenure > 360) {
                          setTenure(360);
                          setTenureInput('360');
                        }
                      }}
                      className={`px-4 py-2 rounded-lg transition-all ${tenureType === 'months' ? 'bg-primary text-white' : 'bg-white/5 text-text-secondary'}`}
                    >
                      Months
                    </button>
                  </div>
                </div>
                {errors.tenure && (
                  <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" /> {errors.tenure}
                  </p>
                )}
                <input
                  type="range"
                  min="1"
                  max={tenureType === 'years' ? 30 : 360}
                  value={tenure}
                  onChange={(e) => {
                    setTenure(parseInt(e.target.value));
                    setTenureInput(e.target.value);
                  }}
                  className="w-full mt-2 accent-primary"
                />
              </div>
            </div>
          </div>

          {/* Results Section */}
          <div className="space-y-6">
            {/* EMI Highlight */}
            <div className="glass-effect rounded-2xl p-6 text-center border-2 border-primary/30">
              <p className="text-text-muted text-sm mb-1">Your Monthly EMI</p>
              <p className="text-5xl font-bold text-primary">{formatCurrency(results.emi)}</p>
              <p className="text-text-secondary text-sm mt-2">per month for {tenure} {tenureType}</p>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 gap-4">
              <div className="glass-effect rounded-xl p-5 text-center">
                <p className="text-text-muted text-xs">Principal Amount</p>
                <p className="text-xl font-bold text-text-primary">{formatCurrency(loanAmount)}</p>
                <p className="text-primary text-sm">{results.principalPercent}%</p>
              </div>
              <div className="glass-effect rounded-xl p-5 text-center">
                <p className="text-text-muted text-xs">Total Interest</p>
                <p className="text-xl font-bold text-red-400">{formatCurrency(results.totalInterest)}</p>
                <p className="text-red-400 text-sm">{results.interestPercent}%</p>
              </div>
            </div>

            <div className="glass-effect rounded-xl p-5 text-center">
              <p className="text-text-muted text-xs">Total Payment</p>
              <p className="text-2xl font-bold text-secondary">{formatCurrency(results.totalPayment)}</p>
            </div>

            {/* Pie Chart */}
            <div className="glass-effect rounded-2xl p-6">
              <h3 className="font-heading text-xl font-bold text-text-primary mb-4">Payment Breakdown</h3>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        {/* Year-wise Table */}
        <div className="mt-8 glass-effect rounded-2xl p-6">
          <h3 className="font-heading text-xl font-bold text-text-primary mb-4">Year-wise Amortization Schedule</h3>
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="sticky top-20 bg-background z-10 text-left py-3 px-4 text-text-muted font-medium">Year</th>
                <th className="sticky top-20 bg-background z-10 text-right py-3 px-4 text-text-muted font-medium">Principal</th>
                <th className="sticky top-20 bg-background z-10 text-right py-3 px-4 text-text-muted font-medium">Interest</th>
                <th className="sticky top-20 bg-background z-10 text-right py-3 px-4 text-text-muted font-medium">Total Paid</th>
                <th className="sticky top-20 bg-background z-10 text-right py-3 px-4 text-text-muted font-medium">Balance</th>
              </tr>
            </thead>
            <tbody>
              {results.yearlyData.slice(0, 15).map((row, index) => (
                <tr key={index} className="border-b border-white/5 hover:bg-white/5">
                  <td className="py-3 px-4 text-text-primary">{row.year}</td>
                  <td className="py-3 px-4 text-right text-primary">{formatCurrency(row.principal)}</td>
                  <td className="py-3 px-4 text-right text-red-400">{formatCurrency(row.interest)}</td>
                  <td className="py-3 px-4 text-right text-text-secondary">{formatCurrency(row.totalPaid)}</td>
                  <td className="py-3 px-4 text-right text-text-muted">{formatCurrency(row.balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {results.yearlyData.length > 15 && (
            <p className="text-text-muted text-sm text-center mt-4">Showing first 15 years...</p>
          )}
        </div>

        {/* Disclaimer */}
        <div className="mt-8 glass-effect rounded-xl p-4 border border-yellow-500/30">
          <p className="text-text-muted text-sm">
            <span className="text-yellow-500 font-semibold">Disclaimer:</span> The EMI calculated is indicative and based on the inputs provided. Actual EMI may vary based on the lender's terms, processing fees, and other charges. Interest rates are subject to change as per market conditions. Please verify with your lender for exact EMI amounts before taking a loan.
          </p>
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <div className="glass-effect rounded-2xl p-8 max-w-2xl mx-auto">
            <h3 className="font-heading text-2xl font-bold text-text-primary mb-4">Need Help with Your Loan?</h3>
            <p className="text-text-secondary mb-6">Get expert guidance on choosing the right loan and managing your EMIs effectively</p>
            <Link to="/contact">
              <Button className="btn-primary text-lg px-8 py-4">
                Get Started <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
